import React, { useState, useEffect } from 'react';

import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  NavItem,
  NavLink,
  Row,
  FormFeedback,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  TabContent,
  TabPane,
} from 'reactstrap';

import { Link } from 'react-router-dom';
import { getGroupDetailsByIdApi } from 'api/group';
//Import Breadcrumb
import Breadcrumbs from '../../components/Common/Breadcrumb';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAllRegionsApi,
  getAllCountiesByRegionApi,
  getConstituencyApi,
  getAllWardsApi,
  getAllRelationshipsApi,
} from 'api/report';
import { addGroup, updateGroup } from 'api/group';
import { useRole } from 'hooks/useRole';
import { SelectField } from 'components/Common/fields';
import notify from 'utils/notify';

const AddGroup = () => {
  const { id } = useParams();
  const { isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isAdmin,
    isSuperAdmin,
    isDirector,
    isData,
    addresses } = useRole();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [regions, setRegions] = useState([]);
  const [counties, setCounties] = useState([]);
  const [wards, setWards] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  //meta title
  document.title = 'Form Wizard | SUN Welfare Administration System';

  const {
    mutate: mutateGroup,
    isLoading: isLoadingGroup,
    data: group,
  } = useMutation((payload) => getGroupDetailsByIdApi(payload), {
    onSuccess: (res) => {
      mutateCounty({ regionId: res.address.regionId });
      mutateSubCounty({ countyId: res.address.countyId })
      mutateWards({ constituencyId: res.address.constituencyId });
    },
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: group?.name || '',
      groupNumber: group?.groupNumber || '',
      description: group?.description || '',
      village: group?.village || '',
      regionId: group?.address?.regionId || '',
      countyId: group?.address?.countyId || '',
      constituencyId: group?.address?.constituencyId || '',
      wardId: group?.address?.wardId || '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please Enter Group Name'),
      groupNumber: Yup.string().required('Please Enter Group Number'),
      regionId: Yup.string().required('Please Select Region'),
      countyId: Yup.string().required('Please Select County'),
      constituencyId: Yup.string().required('Please Select SubCounty'),
      wardId: Yup.string().required('Please Select Ward'),
    }),

    onSubmit: async (values) => {
      let groupData = {
        name: values.name,
        groupNumber: values.groupNumber,
        description: values.description,
        village: values.village,
        address: {
          wardId: values.wardId,
          constituencyId: values.constituencyId,
          countyId: values.countyId,
          regionId: values.regionId,
          village: values.village,
        },
      };
      let resp;
      if (id) {
        groupData['id'] = id;
        resp = await updateGroup(groupData);
      } else {
        resp = await addGroup(groupData);
      }

      if (resp.status === 'Success') {
        notify.success('Group saved successfully').then(() => {
          navigate(-1);
        });
      } else {
        // Handle error case if needed
        notify.error('Error saving group', resp.message || 'An error occurred. Please try again later.');
      }
    },
  });

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const { mutate: mutateRegions } = useMutation(
    (payload) => getAllRegionsApi(payload),
    {
      onSuccess: (res) => {
        if (!(isAdmin || isData || isSuperAdmin)) {
          let regionIds =
            group?.addresses?.map((r) => r?.regionId) ||
            addresses?.map((r) => r?.regionId);
          setRegions(res?.filter((r) => regionIds?.includes(r.id)));
        } else {
          setRegions(res);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const { mutate: mutateCounty } = useMutation(
    (payload) => getAllCountiesByRegionApi(payload),
    {
      onSuccess: (res) => {
        if (!(isAdmin || isData || isSuperAdmin)) {
          let countyIds = addresses?.map((r) => r?.countyId);
          // Step 1: Collect all new data that isn't already in the state
          const newData = res
            ?.filter((r) => countyIds?.includes(r.id))
            ?.filter((c) => !counties.some((item) => item.id === c.id));

          // Step 2: Only update the state if there's new data to add
          if (newData?.length > 0) {
            setCounties((prevData) => [...prevData, ...newData]);
          }
        } else {
          setCounties(res);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const { mutate: mutateSubCounty } = useMutation(
    (payload) => getConstituencyApi(payload),
    {
      onSuccess: (res) => {
        if (!(isAdmin || isData || isSuperAdmin)) {
          let scountyIds = addresses?.map((r) => r?.constituencyId);
          // Step 1: Collect all new data that isn't already in the state
          const newData = res
            ?.filter((r) => scountyIds?.includes(r.id))
            ?.filter((c) => !subCounties.some((item) => item.id === c.id));

          // Step 2: Only update the state if there's new data to add
          if (newData?.length > 0) {
            setSubCounties((prevData) => [...prevData, ...newData]);
          }
        } else {
          setSubCounties(res);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const { mutate: mutateWards } = useMutation(
    (payload) => getAllWardsApi(payload),
    {
      onSuccess: (res) => {
        if (!(isAdmin || isData || isSuperAdmin)) {
          let wardIds = addresses?.map((r) => r?.wardId);
          // Step 1: Collect all new data that isn't already in the state
          const newData = res
            ?.filter((r) => wardIds?.includes(r.id))
            ?.filter((c) => !wards.some((item) => item.id === c.id));

          // Step 2: Only update the state if there's new data to add
          if (newData?.length > 0) {
            setWards((prevData) => [...prevData, ...newData]);
          }
        } else {
          setWards(res);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  useEffect(() => {
    if (id) mutateGroup(id);
  }, [id]);

  useEffect(() => {
    if (isAdmin || isSuperAdmin || isDirector || isData || addresses?.length) {
      mutateRegions({
        countryId: 1,
      });

    }

    const uniqueRegionIds = [...new Set(addresses?.map((a) => a.regionId))];
    const uniqueCountyIds = [...new Set(addresses?.map((a) => a.countyId))];
    const uniqueSubCountyIds = [
      ...new Set(addresses?.map((a) => a.constituencyId)),
    ];
    if (!(isAdmin || !isSuperAdmin || isDirector || isData)) {
      let countyActions = uniqueRegionIds?.map((id) =>
        mutateCounty({ regionId: id }),
      );
      Promise.all(countyActions);
      let subCountyActions = uniqueCountyIds?.map((id) =>
        mutateSubCounty({ countyId: id }),
      );
      Promise.all(subCountyActions);
      let wardActions = uniqueSubCountyIds?.map((id) =>
        mutateWards({ constituencyId: id }),
      );
      Promise.all(wardActions);
    }
  }, [group]);

  const handleRegionChange = (e) => {
    const regionId = e.target.value;
    validation.setFieldValue('regionId', regionId);
    mutateCounty({
      regionId,
    });
  };

  const handleCountyChange = (e) => {
    const countyId = e.target.value;
    validation.setFieldValue('countyId', countyId);
    mutateSubCounty({
      countyId,
    });
  };

  const handleSubCountyChange = (e) => {
    const constituencyId = e.target.value;
    validation.setFieldValue('constituencyId', constituencyId);
    mutateWards({
      constituencyId,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Groups" breadcrumbItem="New Group" />

          <Row>
            <Link to="/groups">
              <Button type="button" color="primary" className="btn">
                <i className="bx bx-arrow-back"></i> Back
              </Button>
            </Link>
            <Col md="7">
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-4">Create New SUN Group</h4>
                  </div>
                  <Form
                    autoComplete="off"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <Row>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label for="name">Group Name</Label>
                          <Input
                            type="text"
                            name="name"
                            className="form-control"
                            id="name"
                            placeholder="Enter Group Name"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.name || ''}
                            invalid={
                              validation.touched.name && validation.errors.name
                                ? true
                                : false
                            }
                          />
                          {validation.touched.name && validation.errors.name ? (
                            <FormFeedback type="invalid">
                              {validation.errors.name}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label for="groupNumber">Group Number</Label>
                          <Input
                            type="text"
                            name="groupNumber"
                            className="form-control"
                            id="groupNumber"
                            onChange={validation.handleChange}
                            placeholder="Enter Group Number"
                            onBlur={validation.handleBlur}
                            value={validation.values.groupNumber || ''}
                            invalid={
                              validation.touched.groupNumber &&
                                validation.errors.groupNumber
                                ? true
                                : false
                            }
                          />
                          {validation.touched.groupNumber &&
                            validation.errors.groupNumber ? (
                            <FormFeedback type="invalid">
                              {validation.errors.groupNumber}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label for="village">Village</Label>
                          <Input
                            type="text"
                            name="village"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.village || ''}
                            className="form-control"
                            id="basicpill-phoneno-input3"
                            placeholder="Enter Village Name"
                          />
                        </div>
                      </Col>
                      <Col lg="6">
                        <div className="mb-3">
                          <Label for="description">Description</Label>
                          <Input
                            type="text"
                            className="form-control"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description || ''}
                            name="description"
                            id="basicpill-phoneno-input3"
                            placeholder="Enter Description"
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <Label htmlFor="regionId">Select Region</Label>
                          <SelectField
                            name="regionId"
                            placeholder="Select Region"
                            onChange={handleRegionChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.regionId || ''}
                            invalid={
                              validation.touched.regionId &&
                                validation.errors.regionId
                                ? true
                                : false
                            }
                          >
                            <option key={''}>Select Region</option>
                            {regions &&
                              regions.map(({ id, name }) => (
                                <option value={id} key={id}>
                                  {name}
                                </option>
                              ))}
                          </SelectField>
                          {validation.touched.regionId &&
                            validation.errors.regionId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.regionId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label htmlFor="role">Select County</Label>
                          <SelectField
                            name="countyId"
                            placeholder="Select County"
                            onChange={handleCountyChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.countyId || ''}
                            invalid={
                              validation.touched.countyId &&
                                validation.errors.countyId
                                ? true
                                : false
                            }
                          >
                            <option key={''}>Select County</option>
                            {counties &&
                              counties.map(({ id, name }) => (
                                <option value={id} key={id}>
                                  {name}
                                </option>
                              ))}
                          </SelectField>
                          {validation.touched.countyId &&
                            validation.errors.countyId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.countyId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label htmlFor="role">Select SubCounty</Label>
                          <SelectField
                            name="constituencyId"
                            placeholder="Select SubCounty"
                            onChange={handleSubCountyChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.constituencyId || ''}
                            invalid={
                              validation.touched.constituencyId &&
                                validation.errors.constituencyId
                                ? true
                                : false
                            }
                          >
                            <option key={''}>Select Sub-County</option>
                            {subCounties &&
                              subCounties.map(({ id, name }) => (
                                <option value={id} key={id}>
                                  {name}
                                </option>
                              ))}
                          </SelectField>
                          {validation.touched.constituencyId &&
                            validation.errors.constituencyId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.constituencyId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label htmlFor="role">Select Ward</Label>
                          <SelectField
                            name="wardId"
                            placeholder="Select Ward"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.wardId || ''}
                            invalid={
                              validation.touched.wardId &&
                                validation.errors.wardId
                                ? true
                                : false
                            }
                          >
                            <option key={''}>Select Ward</option>
                            {wards &&
                              wards.map(({ id, name }) => (
                                <option value={id} key={id}>
                                  {name}
                                </option>
                              ))}
                          </SelectField>
                          {validation.touched.wardId &&
                            validation.errors.wardId ? (
                            <FormFeedback type="invalid">
                              {validation.errors.wardId}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row className="text-end">
                      <Col md="12">
                        <Button color="primary" block type="submit">
                          SUBMIT GROUP
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                  <Modal isOpen={showPopup} toggle={handleClosePopup}>
                    <ModalBody>Group saved successfully.</ModalBody>
                    <ModalFooter>
                      <Button color="primary" onClick={handleClosePopup}>
                        OK
                      </Button>
                    </ModalFooter>
                  </Modal>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AddGroup;
